import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { sanitizeCurrencyInput } from "../lib/utils";
import {
  Ticket, Plus, Search, Copy, Calendar, Users, Percent, DollarSign,
  Pencil, Trash2, X, Check, Clock, TrendingUp, AlertCircle, ChevronDown,
  Filter, BarChart3, ArrowUpRight, ArrowDownRight, Target, Wallet, PieChart,
  RefreshCw, Settings, Hash, Eye, Star, Gift
} from "lucide-react";
import MotoHelmet from "../components/icons/MotoHelmet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { CarregandoBloco, CarregandoCards, CarregandoGrade } from "../components/carregando";
import { CupomForm, modulosDoCupom, rotuloDosModulos, valeEmTodos } from "../features/cupons-modulos";
import { FiltroTipoCliente, limparTipos, nomesTipos, ofertaPassa, tipoDaUrl } from "../features/tipos-cliente";
import { toast } from "sonner";
import axios from "axios";

// Tipos de cliente gravados no cupom (texto JSON ou lista; vazio = todos)
const segmentosDoCupom = (c) => {
  const v = c?.segmentos;
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || "[]") || []; } catch { return []; }
};

const API = "/api";
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Data do cupom em horário local. Cupom antigo grava só o dia ("2023-01-01"):
// `new Date` leria em UTC e no Brasil viraria o dia anterior (31/12/2022), e um
// "válido até" só com o dia expiraria às 21h da véspera. Só o dia = início do
// dia (ou fim do dia, com `fimDoDia`), como o servidor confere.
const dataDoCupom = (v, fimDoDia = false) => {
  if (!v) return null;
  const t = String(v).trim();
  return new Date(t.length === 10 ? `${t}T${fimDoDia ? "23:59:59" : "00:00:00"}` : t);
};

// Carregamento com cards (25/10/2026): os 3 números do topo no mesmo formato
// dos de verdade (ícone + rótulo + número), piscando até os cupons chegarem.
const NumerosCarregando = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-e2e="carregando-cards" aria-busy="true" aria-label="Carregando">
    {[0, 1, 2].map((i) => (
      <div key={i} className="bg-card rounded-xl border p-4 flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-7 w-12" />
        </div>
      </div>
    ))}
  </div>
);

const WEEKDAYS = [
  { value: 0, label: "Dom", fullLabel: "Domingo" },
  { value: 1, label: "Seg", fullLabel: "Segunda" },
  { value: 2, label: "Ter", fullLabel: "Terça" },
  { value: 3, label: "Qua", fullLabel: "Quarta" },
  { value: 4, label: "Qui", fullLabel: "Quinta" },
  { value: 5, label: "Sex", fullLabel: "Sexta" },
  { value: 6, label: "Sáb", fullLabel: "Sábado" },
];

export default function CuponsDesconto() {
  const [coupons, setCoupons] = useState([]);
  // Carregamento com cards só na primeira abertura; ligar/desligar ou salvar
  // um cupom recarrega a lista sem voltar para o esqueleto.
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  // Para quem vale (27/10/2026): filtro da lista e o tipo que chega do botão de ofertas da tela de Clientes
  const [filtroTipo, setFiltroTipo] = useState("");
  const [tipoInicial, setTipoInicial] = useState(null);
  const [params, setParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState("all");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [selectedCouponStats, setSelectedCouponStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);


  const fetchCoupons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coupons`, getAuthHeader());
      setCoupons(res.data || []);
    } catch (error) {
      console.error("Erro ao carregar cupons:", error);
      toast.error("Erro ao carregar cupons");
    } finally {
      setPrimeiraCarga(false);
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const openCreateModal = () => {
    setEditingCoupon(null);
    setTipoInicial(null);
    setShowModal(true);
  };

  // ?para=recorrente: abre "Novo cupom" já para aquele tipo de cliente
  useEffect(() => {
    const para = tipoDaUrl(params);
    if (!para) return;
    setEditingCoupon(null);
    setTipoInicial(para);
    setShowModal(true);
    setParams((p) => { const n = new URLSearchParams(p); n.delete("para"); return n; }, { replace: true });
  }, [params, setParams]);

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setShowModal(true);
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Tem certeza que deseja excluir o cupom "${coupon.code}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/coupons/${coupon.id}`, getAuthHeader());
      toast.success("Cupom excluído com sucesso!");
      fetchCoupons();
    } catch (error) {
      toast.error("Erro ao excluir cupom");
    }
  };

  const handleToggle = async (coupon) => {
    try {
      await axios.patch(`${API}/coupons/${coupon.id}/toggle`, {}, getAuthHeader());
      toast.success(coupon.is_active ? "Cupom desativado" : "Cupom ativado");
      fetchCoupons();
    } catch (error) {
      toast.error("Erro ao alterar status do cupom");
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Código "${code}" copiado!`);
  };

  const openPerformanceModal = async (coupon) => {
    try {
      setLoadingStats(true);
      setShowPerformanceModal(true);
      const res = await axios.get(`${API}/coupons/${coupon.id}/stats`, getAuthHeader());
      // O /stats só traz os números de uso; código, tipo, valor, validade e
      // status vêm do próprio cupom (sem isso o topo saía "Inativo", sem código
      // e com "R$ undefined").
      setSelectedCouponStats({ ...coupon, ...res.data });
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
      setShowPerformanceModal(false);
    } finally {
      setLoadingStats(false);
    }
  };

  // Filtrar cupons
  const filteredCoupons = coupons.filter((coupon) => {
    const matchesSearch =
      (coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (coupon.description || "").toLowerCase().includes(searchTerm.toLowerCase())) &&
      ofertaPassa(segmentosDoCupom(coupon), filtroTipo);

    const now = new Date();
    const validUntil = dataDoCupom(coupon.valid_until, true);
    const isExpired = validUntil && validUntil < now;

    if (filterStatus === "active") return matchesSearch && coupon.is_active && !isExpired;
    if (filterStatus === "inactive") return matchesSearch && !coupon.is_active;
    if (filterStatus === "expired") return matchesSearch && isExpired;

    return matchesSearch;
  });

  // Estatísticas gerais
  const stats = {
    total: coupons.length,
    active: coupons.filter((c) => c.is_active).length,
    totalUsage: coupons.reduce((acc, c) => acc + (c.usage_count || 0), 0),
  };

  const formatDiscount = (coupon) => {
    if (coupon.discount_type === "percentage") {
      return `${coupon.discount_value}%`;
    }
    return `R$ ${coupon.discount_value.toFixed(2).replace(".", ",")}`;
  };

  const getCouponStatus = (coupon) => {
    const now = new Date();
    const validFrom = dataDoCupom(coupon.valid_from);
    const validUntil = dataDoCupom(coupon.valid_until, true);

    if (!coupon.is_active) {
      return { label: "Inativo", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400" };
    }
    if (validUntil && validUntil < now) {
      return { label: "Expirado", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" };
    }
    if (validFrom && validFrom > now) {
      return { label: "Agendado", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" };
    }
    if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
      return { label: "Esgotado", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" };
    }
    return { label: "Ativo", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return dataDoCupom(dateStr).toLocaleDateString("pt-BR");
  };

  const formatCurrency = (value) => {
    return (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getWeekdaysLabel = (weekdays) => {
    if (!weekdays || weekdays.length === 0) return null;
    let days = weekdays;
    if (typeof days === 'string') {
      try { days = JSON.parse(days); } catch (e) { return null; }
    }
    if (days.length === 7) return "Todos os dias";
    return days.map(d => WEEKDAYS.find(w => w.value === d)?.label).join(", ");
  };

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Cupons de Desconto</h1>
            <p className="text-muted-foreground mt-1">Crie e gerencie cupons de desconto para seus clientes</p>
          </div>
          <Button onClick={openCreateModal} className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Cupom
          </Button>
        </div>

        {/* Stats Cards */}
        {primeiraCarga ? <NumerosCarregando /> : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Ticket className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total de Cupons</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cupons Ativos</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total de Usos</p>
                <p className="text-2xl font-bold">{stats.totalUsage}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por código ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="w-4 h-4" />
                {filterStatus === "all" && "Todos"}
                {filterStatus === "active" && "Ativos"}
                {filterStatus === "inactive" && "Inativos"}
                {filterStatus === "expired" && "Expirados"}
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilterStatus("all")}>Todos</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterStatus("active")}>Ativos</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterStatus("inactive")}>Inativos</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterStatus("expired")}>Expirados</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <FiltroTipoCliente valor={filtroTipo} aoMudar={setFiltroTipo} e2e="cupons-filtro-tipo" />
        </div>

        {/* Coupons List */}
        {primeiraCarga ? (
          <CarregandoGrade quantos={6} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" altura="h-[290px]" />
        ) : filteredCoupons.length === 0 ? (
          <div className="bg-card rounded-xl border p-12 text-center">
            <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum cupom encontrado</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || filterStatus !== "all"
                ? "Tente ajustar os filtros de busca"
                : "Crie seu primeiro cupom de desconto"}
            </p>
            {!searchTerm && filterStatus === "all" && (
              <Button onClick={openCreateModal} className="gap-2">
                <Plus className="w-4 h-4" />
                Criar Cupom
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredCoupons.map((coupon) => {
              const status = getCouponStatus(coupon);
              const weekdaysLabel = getWeekdaysLabel(coupon.valid_weekdays);
              return (
                <div
                  key={coupon.id}
                  className={`bg-card rounded-xl border-2 overflow-hidden hover:shadow-lg transition-all ${
                    coupon.is_active ? "border-green-500/30" : "border-gray-300/50 opacity-75"
                  }`}
                >
                  {/* Header com desconto destacado */}
                  <div className={`px-4 py-3 ${
                    coupon.discount_type === "percentage"
                      ? "bg-gradient-to-r from-purple-500/10 to-pink-500/10"
                      : coupon.discount_type === "free_delivery"
                        ? "bg-gradient-to-r from-sky-500/10 to-blue-500/10"
                        : "bg-gradient-to-r from-green-500/10 to-emerald-500/10"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          coupon.discount_type === "percentage"
                            ? "bg-purple-500 text-white"
                            : coupon.discount_type === "free_delivery"
                              ? "bg-sky-500 text-white"
                              : "bg-green-500 text-white"
                        }`}>
                          {coupon.discount_type === "percentage" ? (
                            <span className="text-sm font-bold">{coupon.discount_value}%</span>
                          ) : coupon.discount_type === "free_delivery" ? (
                            <MotoHelmet className="w-5 h-5" />
                          ) : (
                            <DollarSign className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="text-2xl font-bold">
                            {coupon.discount_type === "percentage"
                              ? `${coupon.discount_value}% OFF`
                              : coupon.discount_type === "free_delivery"
                                ? "FRETE GRÁTIS"
                                : `R$ ${coupon.discount_value.toFixed(2).replace(".", ",")}`
                            }
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>

                  {/* Corpo do card */}
                  <div className="p-4">
                    {/* Código */}
                    <div className="flex items-center gap-2 mb-3">
                      <code className="text-lg font-bold bg-muted px-3 py-1.5 rounded-lg flex-1 text-center tracking-wider">
                        {coupon.code}
                      </code>
                      <button
                        onClick={() => handleCopyCode(coupon.code)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Copiar código"
                      >
                        <Copy className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>

                    {/* Descrição */}
                    {coupon.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{coupon.description}</p>
                    )}

                    {/* Info badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {coupon.min_order_value > 0 && (
                        <span className="text-xs bg-muted px-2 py-1 rounded-md">
                          Mín: R$ {coupon.min_order_value.toFixed(2).replace(".", ",")}
                        </span>
                      )}
                      {coupon.valid_until && (
                        <span className="text-xs bg-muted px-2 py-1 rounded-md flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Até {formatDate(coupon.valid_until)}
                        </span>
                      )}
                      {(coupon.time_from || coupon.time_until) && (
                        <span className="text-xs bg-muted px-2 py-1 rounded-md flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {coupon.time_from || "00:00"} - {coupon.time_until || "23:59"}
                        </span>
                      )}
                      {weekdaysLabel && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md">
                          {weekdaysLabel}
                        </span>
                      )}
                      <span className="text-xs bg-muted px-2 py-1 rounded-md flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {coupon.usage_count || 0}{coupon.usage_limit ? `/${coupon.usage_limit}` : ""} usos
                      </span>
                      {(coupon.visible_to_customer === false || coupon.visible_to_customer === 0) && (
                        <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-md">
                          Oculto
                        </span>
                      )}
                      {limparTipos(segmentosDoCupom(coupon)).length > 0 && (
                        <span className="text-xs bg-violet-500/10 text-violet-700 dark:text-violet-300 px-2 py-1 rounded-md font-medium" data-e2e="cupom-so-para">
                          Só para: {nomesTipos(segmentosDoCupom(coupon))}
                        </span>
                      )}
                      {!valeEmTodos(modulosDoCupom(coupon)) && (
                        <span className="text-xs bg-orange-500/10 text-orange-700 dark:text-orange-300 px-2 py-1 rounded-md font-medium" data-e2e="cupom-so-em">
                          Só em: {rotuloDosModulos(modulosDoCupom(coupon))}
                        </span>
                      )}
                    </div>

                    {/* Botão Conferir Desempenho */}
                    <Button
                      variant="outline"
                      className="w-full mb-3 gap-2 border-dashed"
                      onClick={() => openPerformanceModal(coupon)}
                    >
                      <BarChart3 className="w-4 h-4" />
                      Conferir Desempenho
                    </Button>

                    {/* Footer com switch e ações */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={coupon.is_active}
                          onCheckedChange={() => handleToggle(coupon)}
                        />
                        <span className="text-sm text-muted-foreground">
                          {coupon.is_active ? "Ativo" : "Inativo"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(coupon)}
                          title="Editar"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(coupon)}
                          title="Excluir"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Novo / editar cupom: features/cupons-modulos/CupomForm (refeito em 23/10/2026) */}
      <CupomForm open={showModal} cupom={editingCoupon} onSalvo={fetchCoupons} tipoInicial={tipoInicial}
        onClose={() => { setShowModal(false); setEditingCoupon(null); setTipoInicial(null); }} />

      {/* Performance Modal */}
      <Dialog open={showPerformanceModal} onOpenChange={setShowPerformanceModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0" aria-describedby={undefined}>
          {/* Título só para leitor de tela: sem ele o Radix acusa erro no console. */}
          <DialogTitle className="sr-only">Desempenho do cupom</DialogTitle>
          {loadingStats ? (
            <div className="p-6 space-y-4">
              <Skeleton className="h-28 w-full rounded-2xl" />
              <CarregandoCards quantos={4} className="grid grid-cols-2 md:grid-cols-4 gap-4" />
              <CarregandoBloco altura="h-56" />
            </div>
          ) : selectedCouponStats && (
            <div className="space-y-0">
              {/* Header épico */}
              <div className={`relative overflow-hidden p-6 ${
                selectedCouponStats.discount_type === "percentage"
                  ? "bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600"
                  : selectedCouponStats.discount_type === "free_delivery"
                    ? "bg-gradient-to-br from-sky-600 via-blue-600 to-indigo-600"
                    : "bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600"
              }`}>
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 left-0 w-40 h-40 bg-white/30 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                  <div className="absolute bottom-0 right-0 w-60 h-60 bg-white/20 rounded-full translate-x-1/3 translate-y-1/3"></div>
                </div>
                <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                      {selectedCouponStats.discount_type === "percentage" ? (
                        <span className="text-3xl font-black text-white">{selectedCouponStats.discount_value}%</span>
                      ) : selectedCouponStats.discount_type === "free_delivery" ? (
                        <MotoHelmet className="w-10 h-10 text-white" />
                      ) : (
                        <DollarSign className="w-10 h-10 text-white" />
                      )}
                    </div>
                    <div>
                      <code className="text-2xl md:text-3xl font-black text-white tracking-wider">
                        {selectedCouponStats.code}
                      </code>
                      <p className="text-white/80 text-sm mt-1">{selectedCouponStats.description || "Cupom de desconto"}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                          getCouponStatus(selectedCouponStats).label === "Ativo"
                            ? "bg-white text-green-600"
                            : "bg-white/20 text-white"
                        }`}>
                          {getCouponStatus(selectedCouponStats).label}
                        </span>
                        {selectedCouponStats.first_order_only && (
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/20 text-white">
                            1° Pedido
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white/70 text-sm">Desconto Total</p>
                    <p className="text-4xl md:text-5xl font-black text-white">
                      {selectedCouponStats.discount_type === "percentage"
                        ? `${selectedCouponStats.discount_value}% OFF`
                        : selectedCouponStats.discount_type === "free_delivery"
                          ? "FRETE GRÁTIS"
                          : `R$ ${Number(selectedCouponStats.discount_value || 0).toFixed(2).replace(".", ",")}`
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* KPIs principais com gráficos circulares */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Vezes usado - com gráfico circular de progresso */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 rounded-2xl p-4 border border-blue-100 dark:border-blue-900">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <Target className="w-5 h-5 text-white" />
                      </div>
                      {selectedCouponStats.usage_limit && (
                        <div className="relative w-12 h-12">
                          <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="#e5e7eb"
                              strokeWidth="3"
                            />
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="#3b82f6"
                              strokeWidth="3"
                              strokeDasharray={`${Math.min(((selectedCouponStats.total_uses || 0) / selectedCouponStats.usage_limit) * 100, 100)}, 100`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-blue-600">
                            {Math.round(((selectedCouponStats.total_uses || 0) / selectedCouponStats.usage_limit) * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide">Vezes Usado</p>
                    <p className="text-3xl font-black text-blue-700 dark:text-blue-300 mt-1">
                      {selectedCouponStats.total_uses || 0}
                      {selectedCouponStats.usage_limit && (
                        <span className="text-lg text-blue-400 font-normal">/{selectedCouponStats.usage_limit}</span>
                      )}
                    </p>
                  </div>

                  {/* Valor Faturado */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/50 dark:to-emerald-950/50 rounded-2xl p-4 border border-green-100 dark:border-green-900">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30">
                        <Wallet className="w-5 h-5 text-white" />
                      </div>
                      <ArrowUpRight className="w-6 h-6 text-green-500" />
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium uppercase tracking-wide">Valor Faturado</p>
                    <p className="text-2xl font-black text-green-700 dark:text-green-300 mt-1">
                      {formatCurrency(selectedCouponStats.total_revenue)}
                    </p>
                  </div>

                  {/* Clientes Únicos */}
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/50 dark:to-violet-950/50 rounded-2xl p-4 border border-purple-100 dark:border-purple-900">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex -space-x-2">
                        {[...Array(Math.min(selectedCouponStats.unique_customers || 0, 3))].map((_, i) => (
                          <div key={i} className="w-6 h-6 rounded-full bg-purple-200 dark:bg-purple-800 border-2 border-white dark:border-gray-900"></div>
                        ))}
                        {(selectedCouponStats.unique_customers || 0) > 3 && (
                          <div className="w-6 h-6 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center border-2 border-white dark:border-gray-900">
                            +{(selectedCouponStats.unique_customers || 0) - 3}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-400 font-medium uppercase tracking-wide">Clientes Únicos</p>
                    <p className="text-3xl font-black text-purple-700 dark:text-purple-300 mt-1">{selectedCouponStats.unique_customers || 0}</p>
                  </div>

                  {/* Desconto Total */}
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/50 dark:to-rose-950/50 rounded-2xl p-4 border border-red-100 dark:border-red-900">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
                        <Percent className="w-5 h-5 text-white" />
                      </div>
                      <ArrowDownRight className="w-6 h-6 text-red-500" />
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium uppercase tracking-wide">Desconto Dado</p>
                    <p className="text-2xl font-black text-red-700 dark:text-red-300 mt-1">
                      {formatCurrency(selectedCouponStats.total_discount)}
                    </p>
                  </div>
                </div>

                {/* ROI e Comparativo visual */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ROI Card Grande */}
                  <div className={`rounded-2xl p-6 border-2 ${
                    (selectedCouponStats.roi_percentage || 0) > 0
                      ? "bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-950/50 dark:to-green-900/30 border-green-300 dark:border-green-800"
                      : "bg-gradient-to-br from-red-50 to-rose-100 dark:from-red-950/50 dark:to-rose-900/30 border-red-300 dark:border-red-800"
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Retorno sobre Investimento</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">Cada R$ 1 em desconto gerou</p>
                      </div>
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        (selectedCouponStats.roi_percentage || 0) > 0
                          ? "bg-green-500 shadow-lg shadow-green-500/40"
                          : "bg-red-500 shadow-lg shadow-red-500/40"
                      }`}>
                        {(selectedCouponStats.roi_percentage || 0) > 0 ? (
                          <TrendingUp className="w-8 h-8 text-white" />
                        ) : (
                          <ArrowDownRight className="w-8 h-8 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <p className={`text-6xl font-black ${
                        (selectedCouponStats.roi_percentage || 0) > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }`}>
                        {(selectedCouponStats.roi_percentage || 0).toFixed(0)}
                      </p>
                      <span className={`text-3xl font-bold ${
                        (selectedCouponStats.roi_percentage || 0) > 0 ? "text-green-500" : "text-red-500"
                      }`}>%</span>
                    </div>
                    <p className={`text-sm mt-2 ${
                      (selectedCouponStats.roi_percentage || 0) > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                    }`}>
                      {(selectedCouponStats.roi_percentage || 0) > 0
                        ? `R$ ${((selectedCouponStats.roi_percentage || 0) / 100).toFixed(2).replace(".", ",")} de retorno por R$ 1 investido`
                        : "Cupom ainda não gerou retorno positivo"
                      }
                    </p>
                  </div>

                  {/* Comparativo Faturamento vs Desconto */}
                  <div className="bg-card rounded-2xl p-6 border">
                    <p className="text-sm font-semibold mb-4">Faturamento vs Desconto</p>
                    <div className="space-y-4">
                      {/* Barra de faturamento */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-green-600 dark:text-green-400 font-medium">Faturamento</span>
                          <span className="font-bold">{formatCurrency(selectedCouponStats.total_revenue)}</span>
                        </div>
                        <div className="h-8 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg transition-all duration-1000 flex items-center justify-end pr-2"
                            style={{ width: `${Math.min(100, ((selectedCouponStats.total_revenue || 0) / Math.max((selectedCouponStats.total_revenue || 0), (selectedCouponStats.total_discount || 0), 1)) * 100)}%` }}
                          >
                            <span className="text-xs font-bold text-white">
                              {((selectedCouponStats.total_revenue || 0) / Math.max((selectedCouponStats.total_revenue || 0) + (selectedCouponStats.total_discount || 0), 1) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Barra de desconto */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-red-600 dark:text-red-400 font-medium">Desconto</span>
                          <span className="font-bold">{formatCurrency(selectedCouponStats.total_discount)}</span>
                        </div>
                        <div className="h-8 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-lg transition-all duration-1000 flex items-center justify-end pr-2"
                            style={{ width: `${Math.min(100, ((selectedCouponStats.total_discount || 0) / Math.max((selectedCouponStats.total_revenue || 0), (selectedCouponStats.total_discount || 0), 1)) * 100)}%` }}
                          >
                            <span className="text-xs font-bold text-white">
                              {((selectedCouponStats.total_discount || 0) / Math.max((selectedCouponStats.total_revenue || 0) + (selectedCouponStats.total_discount || 0), 1) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Métricas extras */}
                    <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Ticket Médio</p>
                        <p className="text-xl font-bold">{formatCurrency(selectedCouponStats.avg_order_value)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Desconto Médio</p>
                        <p className="text-xl font-bold">{formatCurrency(selectedCouponStats.avg_discount)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gráfico de uso por dia da semana - Melhorado */}
                {selectedCouponStats.usage_by_weekday && Object.keys(selectedCouponStats.usage_by_weekday).length > 0 && (
                  <div className="bg-card border rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="font-bold text-lg">Uso por Dia da Semana</h4>
                        <p className="text-sm text-muted-foreground">Descubra quando seu cupom é mais utilizado</p>
                      </div>
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex items-end justify-between gap-3 h-48">
                      {WEEKDAYS.map((day) => {
                        const count = selectedCouponStats.usage_by_weekday[day.value] || 0;
                        const maxCount = Math.max(...Object.values(selectedCouponStats.usage_by_weekday), 1);
                        const height = (count / maxCount) * 100;
                        const isMax = count === maxCount && count > 0;
                        return (
                          <div key={day.value} className="flex-1 flex flex-col items-center group">
                            <div className={`mb-2 px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                              isMax
                                ? "bg-primary text-primary-foreground scale-110"
                                : "bg-muted text-muted-foreground group-hover:bg-primary/10"
                            }`}>
                              {count}
                            </div>
                            <div className="w-full bg-muted rounded-xl overflow-hidden" style={{ height: '140px' }}>
                              <div className="w-full h-full flex items-end">
                                <div
                                  className={`w-full rounded-t-xl transition-all duration-700 ${
                                    isMax
                                      ? "bg-gradient-to-t from-primary to-primary/60"
                                      : "bg-gradient-to-t from-blue-500/70 to-blue-400/50 group-hover:from-blue-600/80 group-hover:to-blue-500/60"
                                  }`}
                                  style={{ height: `${Math.max(height, 8)}%` }}
                                />
                              </div>
                            </div>
                            <span className={`text-sm mt-2 font-medium ${isMax ? "text-primary" : "text-muted-foreground"}`}>
                              {day.label}
                            </span>
                            <span className="text-[10px] text-muted-foreground">{day.fullLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Gráfico de linha - Últimos 30 dias */}
                {selectedCouponStats.usage_by_day && selectedCouponStats.usage_by_day.length > 0 && (
                  <div className="bg-card border rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="font-bold text-lg">Tendência de Uso</h4>
                        <p className="text-sm text-muted-foreground">Últimos 30 dias de atividade</p>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                        <span className="text-muted-foreground">Usos diários</span>
                      </div>
                    </div>
                    <div className="relative h-40">
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="border-b border-dashed border-gray-200 dark:border-gray-800"></div>
                        ))}
                      </div>
                      {/* Chart */}
                      <svg className="w-full h-full" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        {/* Area */}
                        <path
                          d={(() => {
                            const data = selectedCouponStats.usage_by_day;
                            const maxCount = Math.max(...data.map(d => d.count), 1);
                            const points = data.map((d, i) => {
                              const x = (i / (data.length - 1)) * 100;
                              const y = 100 - ((d.count / maxCount) * 100);
                              return `${x}%,${y}%`;
                            });
                            return `M0%,100% L${points.join(' L')} L100%,100% Z`;
                          })()}
                          fill="url(#chartGradient)"
                        />
                        {/* Line */}
                        <path
                          d={(() => {
                            const data = selectedCouponStats.usage_by_day;
                            const maxCount = Math.max(...data.map(d => d.count), 1);
                            const points = data.map((d, i) => {
                              const x = (i / (data.length - 1)) * 100;
                              const y = 100 - ((d.count / maxCount) * 100);
                              return `${x}%,${y}%`;
                            });
                            return `M${points.join(' L')}`;
                          })()}
                          fill="none"
                          stroke="rgb(59, 130, 246)"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        {/* Points */}
                        {selectedCouponStats.usage_by_day.map((d, i) => {
                          const data = selectedCouponStats.usage_by_day;
                          const maxCount = Math.max(...data.map(dd => dd.count), 1);
                          const x = (i / (data.length - 1)) * 100;
                          const y = 100 - ((d.count / maxCount) * 100);
                          if (d.count === 0) return null;
                          return (
                            <circle
                              key={i}
                              cx={`${x}%`}
                              cy={`${y}%`}
                              r="4"
                              fill="white"
                              stroke="rgb(59, 130, 246)"
                              strokeWidth="2"
                              className="hover:r-6 transition-all"
                            >
                              <title>{d.date}: {d.count} uso(s)</title>
                            </circle>
                          );
                        })}
                      </svg>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-4 px-1">
                      <span>{selectedCouponStats.usage_by_day[0]?.date}</span>
                      <span className="font-medium text-foreground">
                        Total: {selectedCouponStats.usage_by_day.reduce((acc, d) => acc + d.count, 0)} usos
                      </span>
                      <span>{selectedCouponStats.usage_by_day[selectedCouponStats.usage_by_day.length - 1]?.date}</span>
                    </div>
                  </div>
                )}

                {/* Últimos usos - Melhorado */}
                {selectedCouponStats.recent_usage && selectedCouponStats.recent_usage.length > 0 && (
                  <div className="bg-card border rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-bold text-lg">Últimas Utilizações</h4>
                        <p className="text-sm text-muted-foreground">Histórico recente de uso do cupom</p>
                      </div>
                      <span className="text-xs bg-muted px-2 py-1 rounded-full">
                        {selectedCouponStats.recent_usage.length} registro(s)
                      </span>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                      {selectedCouponStats.recent_usage.map((usage, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-gradient-to-r from-muted/50 to-transparent rounded-xl px-4 py-3 hover:from-muted transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              idx === 0 ? "bg-green-500/10 text-green-600" : "bg-muted"
                            }`}>
                              <Clock className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {new Date(usage.used_at).toLocaleDateString("pt-BR", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric"
                                })}
                                <span className="text-muted-foreground ml-2">
                                  {new Date(usage.used_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                              </p>
                              {usage.order_total > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  Pedido de {formatCurrency(usage.order_total)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-red-600 dark:text-red-400">
                              -{formatCurrency(usage.discount_applied)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Informações de validade */}
                {(selectedCouponStats.valid_from || selectedCouponStats.valid_until) && (
                  <div className="bg-muted/30 rounded-xl p-4">
                    <div className="flex flex-wrap gap-6 text-sm">
                      {selectedCouponStats.valid_from && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Início:</span>
                          <span className="font-medium">{formatDate(selectedCouponStats.valid_from)}</span>
                        </div>
                      )}
                      {selectedCouponStats.valid_until && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Término:</span>
                          <span className="font-medium">{formatDate(selectedCouponStats.valid_until)}</span>
                        </div>
                      )}
                      {selectedCouponStats.time_from && selectedCouponStats.time_until && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Horário:</span>
                          <span className="font-medium">{selectedCouponStats.time_from} - {selectedCouponStats.time_until}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
